import { classes, events } from '@theme/constants';
import { getRefs } from '@theme/dom';
import { publish } from '@theme/event-bus';
import { fetchConfig, json } from '@theme/fetch';
import {
  debounce,
  getCartSectionNames,
  getUrlParam,
  serializeRichTextNodeHTML,
  setInputValue,
  toTitleCase,
} from '@theme/helpers';
import { HTMLCustomElement, HTMLShopifySectionElement } from '@theme/html-custom-element';

class SubscriptionBuilder extends HTMLShopifySectionElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('click', this.refs.start && this.refs.start.all, this.handleStart.bind(this));
    this.on('show', this.steps, this.handleStepShow.bind(this));
    this.on('option-change', this.steps, this.handleStepChange.bind(this));
    this.on('click', this.refs.addToCart, this.handleAddToCart.bind(this));
    this.on('click', this.refs.breadcrumb && this.refs.breadcrumb.all, this.handleBreadcrumbClick.bind(this));
    this.on('scroll', window, debounce(this.handleWindowScroll.bind(this)));

    if (getUrlParam('recipient')) {
      requestAnimationFrame(() => {
        this.goToStep('recipient', getUrlParam('recipient'));
      });
    }

    /**
     * Show add on products in the summary if their quick add has been clicked.
     */
    this.on('variant-select', this.addOnProductsQuickAddElements, (event) => {
      for (const element of this.subscriptionBuilderAddOns) {
        if (event.target.getAttribute('product-handle') === element.getAttribute('product-handle')) {
          if (!element.refs.id.disabled) {
            element.refs.quantity.closest('quantity-selector').adjustValue(1);
          } else {
            element.refs.id.disabled = false;
          }

          this.updateSummary();
        }
      }
    });
  }

  /**
   * @returns {SubscriptionBuilderStep[]}
   */
  get steps() {
    return Array.from(this.querySelectorAll('subscription-builder-step'));
  }

  /**
   * Returns the selections for each step.
   *
   * @returns {{
   *  recipient: string;
   *  ['order-type']: string;
   *  ['pods-collection']: string;
   *  ['pods-variant']: string;
   *  ['beans-brew-type']: string;
   *  ['beans-product']: string;
   *  ['beans-variant']: string;
   *  quantity: string;
   *  frequency: string;
   * }}
   */
  get selections() {
    return this.steps.reduce(
      (steps, { name, value }) => ({
        ...steps,
        [name]: value,
      }),
      {},
    );
  }

  /**
   * Returns the selected product to add to cart.
   *
   * @returns {{
   *  handle: string;
   *  selectedVariant: string;
   * }}
   */
  get selectedProduct() {
    let handle;
    let selectedVariant;

    if (this.selections['order-type'] === 'Pods') {
      const podsVariantStep = this.steps.find(({ name }) => name === 'pods-variant');

      if (podsVariantStep.selected) {
        handle = podsVariantStep.selected.refs.radio.dataset.handle;
        selectedVariant = podsVariantStep.value;
      }
    }

    if (this.selections['order-type'] === 'Beans') {
      handle = this.selections['beans-product'];
      selectedVariant = this.selections['beans-variant'];
    }

    return {
      handle,
      selectedVariant,
    };
  }

  /**
   * Returns the current step.
   * @returns {SubscriptionBuilderStep}
   */
  get current() {
    return this.steps.find((step) => step.classList.contains(classes.active));
  }

  /**
   * @returns {import('@theme/quick-add').QuickAdd[]}
   */
  get addOnProductsQuickAddElements() {
    return Array.from(this.querySelectorAll('quick-add'));
  }

  /**
   * @returns {SubscriptionBuilderAddOn[]}
   */
  get subscriptionBuilderAddOns() {
    return Array.from(this.querySelectorAll('subscription-builder-add-on'));
  }

  /**
   * Returns the selected product and variant.
   */
  async getSelectedProduct() {
    let handle;
    let selectedVariant;
    let id;
    let sellingPlanAllocation;

    if (this.selections['order-type'] === 'Pods') {
      const podsVariantStep = this.steps.find(({ name }) => name === 'pods-variant');

      if (podsVariantStep.selected) {
        handle = podsVariantStep.selected.refs.radio.dataset.handle;
        selectedVariant = podsVariantStep.value;
      }
    }

    if (this.selections['order-type'] === 'Beans') {
      handle = this.selections['beans-product'];
      selectedVariant = this.selections['beans-variant'];
    }

    if (selectedVariant) {
      const { id: variantId, sellingPlanAllocations } = await queryStorefrontApi(PRODUCT_PRICING_QUERY, {
        handle,
      }).then(
        ({
          data: {
            product: {
              variants: { nodes: variants },
            },
          },
        }) => variants.find(({ id }) => id === `gid://shopify/ProductVariant/${selectedVariant}`),
      );

      sellingPlanAllocation = sellingPlanAllocations.nodes.find(
        ({ sellingPlan }) => sellingPlan.id === `gid://shopify/SellingPlan/${this.selections.frequency}`,
      );

      id = variantId;
    }

    return {
      handle,
      variant: selectedVariant && {
        id: id.replace('gid://shopify/ProductVariant/', ''),
        price: sellingPlanAllocation && parseFloat(sellingPlanAllocation.price.amount) * 100,
      },
    };
  }

  /**
   * @param {MouseEvent} event
   */
  handleBreadcrumbClick(event) {
    const targetStep = this.steps.find(({ name }) => name === event.currentTarget.dataset.step);

    if (!(this.selections[event.currentTarget.dataset.step] === undefined)) {
      this.goToStep(event.currentTarget.dataset.step);
    }

    /**
     * Resets all steps after the target one.
     */
    for (const step of this.steps.slice(this.steps.indexOf(targetStep) + 1)) {
      step.reset();
    }
  }

  /**
   * Handles the start click event.
   */
  handleStart() {
    this.goToStep('recipient');
  }

  /**
   * @param {string} name - The name of the step to show.
   * @param {any} [value]
   */
  goToStep(name, value) {
    this.scrollIntoView({ block: 'start' });

    for (const step of this.steps) {
      if (step.name === name) {
        step.show(value, !(value && !step.refs.next));
        continue;
      }

      step.hide();
    }
  }

  /**
   * Handles the step shown event.
   */
  handleStepShow() {
    this.classList.toggle(classes.active, !!this.current && !(this.current === 'cover'));
    this.classList.toggle(classes.complete, this.current.name === 'end');

    this.updateBreadcrumbs();
  }

  /**
   * Handles the step change event.
   */
  handleStepChange() {
    this.updateSummary();
  }

  /**
   * Updates the breadcrumbs states.
   */
  updateBreadcrumbs() {
    for (const breadcrumb of this.refs.breadcrumb.all) {
      breadcrumb.classList.toggle(classes.active, breadcrumb.dataset.step === this.current.name);

      if (!(this.current.name === 'end') && breadcrumb.hasAttribute('data-order-type')) {
        if (this.selections['order-type']) {
          breadcrumb.classList.toggle(
            classes.hidden,
            !(breadcrumb.dataset.orderType === this.selections['order-type']),
          );
        } else {
          if (
            breadcrumb === breadcrumb.parentNode.querySelector(`[data-order-type="${breadcrumb.dataset.orderType}"]`)
          ) {
            breadcrumb.classList.remove(classes.hidden);
          } else {
            breadcrumb.classList.add(classes.hidden);
          }
        }
      }
    }
  }

  /**
   * Updates the summary elements.
   */
  async updateSummary() {
    const { variant } = await this.getSelectedProduct();
    let total = 0;

    for (const element of this.refs.summaryItem.all) {
      let text = this.selections[element.dataset.step];

      /**
       * Steps which should show their heading instead of their value.
       */
      const headingSteps = ['recipient', 'beans-product', 'beans-variant', 'pods-variant', 'frequency'];

      for (const stepName of headingSteps) {
        if (element.dataset.step === stepName) {
          const step = this.steps.find(({ name }) => name === stepName);

          if (step.selected) {
            text = step.selected.refs.radio.dataset.heading;
          }
        }
      }

      element.textContent = text;
      element.parentElement.classList.toggle(classes.hidden, !text);
    }

    if (variant) {
      this.refs.subtotal.innerHTML = new Intl.NumberFormat(`${window.Shopify.locale}-${window.Shopify.country}`, {
        style: 'currency',
        currency: window.Shopify.currency.active,
      }).format((variant.price * parseInt(this.selections.quantity)) / 100);

      total += variant.price * parseInt(this.selections.quantity);
    }

    for (const element of this.subscriptionBuilderAddOns) {
      if (!element.refs.id.disabled) {
        total += parseFloat(element.getAttribute('price') * parseInt(element.refs.quantity.value));
      }
    }

    this.refs.total.innerHTML = new Intl.NumberFormat(`${window.Shopify.locale}-${window.Shopify.country}`, {
      style: 'currency',
      currency: window.Shopify.currency.active,
    }).format(total / 100);
  }

  /**
   * Handles the add to cart event.
   */
  async handleAddToCart() {
    const { variant } = await this.getSelectedProduct();
    if (!variant) {
      throw new Error('A variant has not been selected');
    }

    const items = [
      {
        id: variant.id,
        quantity: parseInt(this.selections.quantity),
        selling_plan: this.selections.frequency,
        properties: {
          _recipient: this.selections.recipient,
        },
      },
    ];

    for (const element of this.subscriptionBuilderAddOns) {
      if (!element.refs.id.disabled) {
        items.push({
          id: element.refs.id.value,
          quantity: parseInt(element.refs.quantity.value),
        });
      }
    }

    this.refs.addToCart.disabled = true;

    json(
      fetch(
        `${theme.routes.cart_add_url}.js`,
        fetchConfig('json', {
          body: JSON.stringify({
            items,
            sections: getCartSectionNames(),
          }),
        }),
      ),
    ).then((response) => {
      if (!(response.status === 'bad_request')) {
        publish(events.cartUpdated);
        publish(events.cartAdded);
        publish(events.sectionUpdate, { sections: response.sections });
      }

      this.refs.addToCart.disabled = false;
    });
  }

  /**
   * Handles the window scroll event.
   */
  handleWindowScroll() {
    const staticStart = this.refs.start.all.find(({ dataset }) => dataset.startType === 'static');
    const fixedStart = this.refs.start.all.find(({ dataset }) => dataset.startType === 'fixed');

    if (!staticStart || !fixedStart) {
      return;
    }

    const { bottom: submitBottom } = staticStart.getBoundingClientRect();
    const { bottom: mainBottom } = document.querySelector('.content-for-layout').getBoundingClientRect();
    const submitIsAboveViewport =
      submitBottom < (parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) ?? 0);
    const mainIsPastViewportBottom = mainBottom < window.innerHeight;

    fixedStart.classList.toggle(classes.active, submitIsAboveViewport && !mainIsPastViewportBottom);
  }

  onBlockSelect(event) {
    if (event.target.tagName.toLowerCase() === 'subscription-builder-step') {
      this.goToStep(event.target.getAttribute('name'));
    }
  }
}

class SubscriptionBuilderStep extends HTMLCustomElement {
  requiredAttributes = ['name'];

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('value-checked', this, this.handleOptionEvent.bind(this));
    this.on('change', this.refs.input, this.handleOptionEvent.bind(this));
    this.on('click', this.refs.next, this.handleNextButtonEvent.bind(this));
  }

  /**
   * Returns the name of the step.
   * @returns {string}
   */
  get name() {
    return this.getAttribute('name');
  }

  /**
   * @returns {SubscriptionBuilder}
   */
  get subscriptionBuilder() {
    return this.closest('subscription-builder');
  }

  /**
   * @returns {SubscriptionBuilderOption[]}
   */
  get options() {
    return Array.from(this.querySelectorAll('subscription-builder-option'));
  }

  /**
   * Returns the selected radio element.
   * @returns {SubscriptionBuilderOption}
   */
  get selected() {
    return this.options.find((option) => option.checked);
  }

  /**
   * Returns the selected value of the step.
   */
  get value() {
    if (this.selected) {
      return this.selected.value;
    }

    if (this.refs.input) {
      if (this.refs.input.value === '') {
        return undefined;
      }

      return this.refs.input.value;
    }

    return undefined;
  }

  /**
   * Returns the next step name to navigate to.
   * @returns {string}
   */
  get nextStep() {
    if (this.selected && this.selected.hasAttribute('next-step')) {
      return this.selected.getAttribute('next-step');
    }

    return this.getAttribute('next-step');
  }

  /**
   * @returns {SubscriptionBuilderHero[]}
   */
  get heroElements() {
    return this.querySelectorAll('subscription-builder-hero');
  }

  /**
   * @param {CustomEvent} event
   */
  handleOptionEvent(event) {
    event.stopPropagation();

    this.dispatchEvent(new CustomEvent('option-change', { detail: event.detail }));
    this.showCurrentHero();

    if (!this.refs.next) {
      this.handleNextButtonEvent();
      return;
    }

    this.refs.next.disabled = !this.value;
  }

  /**
   * Shows the current hero element.
   */
  showCurrentHero() {
    if (!this.value) return;

    for (const [index, element] of this.heroElements.entries()) {
      element.classList.toggle(
        classes.active,
        this.options.length ? element.getAttribute('option-value') === this.value : index === 0,
      );
    }
  }

  /**
   * Resets the step's value.
   */
  reset() {
    if (this.refs.input) {
      this.refs.input.value = '';
    }

    for (const option of this.options) {
      option.refs.radio.checked = false;
    }
  }

  /**
   * Handles the next button click event.
   */
  handleNextButtonEvent() {
    if (this.nextStep && !!this.value) {
      requestAnimationFrame(() => {
        this.subscriptionBuilder.goToStep(this.nextStep);
      });
    }
  }

  /**
   * Shows the step.
   */
  async show(value, applyActiveClass = true) {
    if (applyActiveClass) {
      this.classList.add(classes.active);
    }

    if (value) {
      if (this.refs.input) {
        this.refs.input.value = value;
      } else {
        for (const option of this.options) {
          for (const radio of option.refs.radio.all) {
            setInputValue(radio, radio.value === toTitleCase(value), 'checked');
          }
        }
      }
    }

    if (this.name === 'pods-variant') {
      const podsCollectionStep = this.subscriptionBuilder.steps.find(({ name }) => name === 'pods-collection');

      if (podsCollectionStep) {
        queryStorefrontApi(PODS_PRODUCT_QUERY, {
          tag: getPodsProductTagFilter(podsCollectionStep.selected.value, podsCollectionStep.options),
        }).then(
          ({
            data: {
              products: {
                nodes: [
                  {
                    handle,
                    variants: { nodes: variants },
                  },
                ],
              },
            },
          }) => {
            this.refs.options.innerHTML = variants
              .map(
                ({ title, id, subscriptionBuilderHeading, subscriptionBuilderSubheading, subscriptionBuilderBody }) => {
                  const value = id.replace('gid://shopify/ProductVariant/', '');

                  return renderOption(
                    {
                      heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                      subheading: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                      body: subscriptionBuilderBody && serializeRichTextNodeHTML(subscriptionBuilderBody.value),
                      value,
                      stepName: this.name,
                      id: `${this.name}-${value}`,
                      dataset: {
                        handle,
                      },
                    },
                    this.subscriptionBuilder,
                  ).outerHTML;
                },
              )
              .join('\n');

            for (const { id, title, subscriptionBuilderHeading, subscriptionBuilderSubheading } of variants) {
              this.refs.hero.appendChild(
                renderHero(
                  {
                    optionValue: id.replace('gid://shopify/ProductVariant/', ''),
                    heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                    body: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                  },
                  this.subscriptionBuilder,
                ),
              );
            }
          },
        );
      }
    }

    if (this.name === 'beans-product') {
      const beansBrewTypeStep = this.subscriptionBuilder.steps.find(({ name }) => name === 'beans-brew-type');
      const recipient = this.subscriptionBuilder.selections.recipient;
      let tag = `${beansBrewTypeStep.selected.value} ${recipient}`;

      if (recipient === 'Gift') {
        tag = 'Gift';
      }

      queryStorefrontApi(BEANS_PRODUCTS_QUERY).then(
        ({
          data: {
            collection: {
              products: { nodes: products },
            },
          },
        }) => {
          products = products
            .filter(
              ({ subscriptionBuilderTag }) =>
                subscriptionBuilderTag && subscriptionBuilderTag.value.split(', ').includes(tag),
            )
            .sort(
              (a, b) =>
                (a.subscriptionBuilderPriority?.value ?? Infinity) - (b.subscriptionBuilderPriority?.value ?? Infinity),
            );

          this.refs.options.innerHTML = products
            .map(
              ({ title, handle, subscriptionBuilderHeading, subscriptionBuilderSubheading, subscriptionBuilderBody }) =>
                renderOption(
                  {
                    heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                    subheading: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                    body: subscriptionBuilderBody && serializeRichTextNodeHTML(subscriptionBuilderBody.value),
                    value: handle,
                    stepName: this.name,
                    id: `${this.name}-${handle}`,
                  },
                  this.subscriptionBuilder,
                ).outerHTML,
            )
            .join('\n');

          for (const { handle, title, subscriptionBuilderHeading, subscriptionBuilderSubheading } of products) {
            this.refs.hero.appendChild(
              renderHero(
                {
                  optionValue: handle,
                  heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                  body: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                },
                this.subscriptionBuilder,
              ),
            );
          }
        },
      );
    }

    if (this.name === 'beans-variant') {
      const beansProductStep = this.subscriptionBuilder.steps.find(({ name }) => name === 'beans-product');

      queryStorefrontApi(BEANS_VARIANTS_QUERY, {
        handle: beansProductStep.selected.value,
      }).then(
        ({
          data: {
            product: {
              variants: { nodes: variants },
            },
          },
        }) => {
          const [variant] = variants;

          this.refs.options.innerHTML = variants
            .map(
              ({ title, id, subscriptionBuilderHeading, subscriptionBuilderSubheading, subscriptionBuilderBody }) => {
                const value = id.replace('gid://shopify/ProductVariant/', '');

                return renderOption(
                  {
                    heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                    subheading: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                    body: subscriptionBuilderBody && serializeRichTextNodeHTML(subscriptionBuilderBody.value),
                    value,
                    stepName: this.name,
                    id: `${this.name}-${value}`,
                  },
                  this.subscriptionBuilder,
                ).outerHTML;
              },
            )
            .join('\n');

          for (const { id, title, subscriptionBuilderHeading, subscriptionBuilderSubheading } of variants) {
            this.refs.hero.appendChild(
              renderHero(
                {
                  optionValue: id.replace('gid://shopify/ProductVariant/', ''),
                  heading: subscriptionBuilderHeading ? subscriptionBuilderHeading.value : title,
                  body: subscriptionBuilderSubheading && subscriptionBuilderSubheading.value,
                },
                this.subscriptionBuilder,
              ),
            );
          }

          /**
           * If only the default variant, select it and go to the next step.
           */
          if (variant.title === 'Default Title') {
            const [option] = this.options;

            if (option) {
              setInputValue(option.refs.radio, true, 'checked');
            }

            this.subscriptionBuilder.goToStep(this.nextStep);
          }
        },
      );
    }

    if (this.name === 'quantity') {
      if (!this.value) {
        this.refs.input.value = 1;
      }
    }

    if (this.name === 'frequency') {
      const { handle, variant: selectedVariant } = await this.subscriptionBuilder.getSelectedProduct();

      queryStorefrontApi(FREQUENCY_PRODUCT_QUERY, {
        handle,
      }).then(
        ({
          data: {
            product: {
              variants: { nodes: variants },
            },
          },
        }) => {
          const variant =
            selectedVariant &&
            variants.find(async ({ id }) => id === `gid://shopify/ProductVariant/${selectedVariant.id}`);

          if (variant) {
            this.refs.options.innerHTML = variant.sellingPlanAllocations.nodes
              .sort(sortSellingPlanAllocations)
              .map(
                ({ sellingPlan }) =>
                  renderOption(
                    {
                      heading: sellingPlan.name,
                      stepName: this.name,
                      value: sellingPlan.id.replace('gid://shopify/SellingPlan/', ''),
                    },
                    this.subscriptionBuilder,
                  ).outerHTML,
              )
              .join('\n');

            for (const { sellingPlan } of variant.sellingPlanAllocations.nodes.sort(sortSellingPlanAllocations)) {
              this.refs.hero.appendChild(
                renderHero(
                  {
                    optionValue: sellingPlan.id.replace('gid://shopify/SellingPlan/', ''),
                    heading: sellingPlan.name,
                  },
                  this.subscriptionBuilder,
                ),
              );
            }
          }
        },
      );
    }

    if (this.name === 'end') {
      const {
        ['order-type']: orderType,
        ['beans-product']: beansProduct,
        ['beans-variant']: beansVariant,
        ['pods-variant']: podsVariant,
      } = this.subscriptionBuilder.selections;

      if (orderType === 'Beans') {
        // eslint-disable-next-line no-console
        console.log(`Add product ${beansProduct} with variant ${beansVariant}`);
      }

      if (orderType === 'Pods') {
        // eslint-disable-next-line no-console
        console.log(`Add variant ${podsVariant}`);
      }
    }

    this.dispatchEvent(new CustomEvent('show'));
    this.showCurrentHero();
  }

  /**
   * Hides the step.
   */
  hide() {
    this.classList.remove(classes.active);
  }
}

class SubscriptionBuilderOption extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('change', this.refs.radio, this.handleRadioEvent.bind(this));
  }

  /**
   * Returns if the option is checked.
   * @returns {boolean}
   */
  get checked() {
    return this.refs.radio.checked;
  }

  /**
   * Returns the option value.
   * @returns {string}
   */
  get value() {
    return this.refs.radio.value;
  }

  /**
   * @param {InputEvent} event
   */
  handleRadioEvent(event) {
    event.stopPropagation();

    if (this.checked) {
      this.dispatchEvent(new CustomEvent('value-checked', { detail: event, bubbles: true }));
    }
  }
}

class SubscriptionBuilderHero extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();
  }
}

/**
 * Renders a step option using the template.
 *
 * @param {object} option
 * @param {string} [option.stepName]
 * @param {string} [option.id]
 * @param {string} [option.value]
 * @param {string} [option.nextStep]
 * @param {string} [option.heading]
 * @param {string} [option.subheading]
 * @param {string} [option.body]
 * @param {{ [key: string]: string }} [option.dataset]
 * @param {SubscriptionBuilder} subscriptionBuilder
 */
function renderOption({ stepName, id, value, nextStep, dataset, ...rest }, subscriptionBuilder) {
  /** @type {HTMLTemplateElement} */
  const template = subscriptionBuilder.refs.optionTemplate.content.cloneNode(true);
  /** @type {SubscriptionBuilderOption} */
  const element = template.firstElementChild;
  const refs = getRefs(element);

  if (stepName) refs.radio.name = stepName;
  if (id) refs.radio.id = id;
  if (value) refs.radio.value = value;
  if (nextStep) element.setAttribute('next-step', nextStep);
  if (rest.heading) refs.radio.dataset.heading = rest.heading;

  if (rest.subheading) {
    refs.subheading.innerHTML = rest.subheading;
  } else {
    refs.footer.remove();
  }

  if (dataset) {
    for (const property of Object.keys(dataset)) {
      refs.radio.dataset[property] = dataset[property];
    }
  }

  for (const element of ['heading', 'body']) {
    if (rest[element]) {
      refs[element].innerHTML = rest[element];
      continue;
    }

    refs[element].remove();
  }

  return element;
}

/**
 * Renders a step hero using the template.
 *
 * @param {object} hero
 * @param {string} [hero.optionValue]
 * @param {string} [hero.heading]
 * @param {string} [hero.body]
 * @param {SubscriptionBuilder} subscriptionBuilder
 */
function renderHero({ optionValue, ...rest }, subscriptionBuilder) {
  /** @type {HTMLTemplateElement} */
  const template = subscriptionBuilder.refs.heroTemplate.content.cloneNode(true);
  /** @type {SubscriptionBuilderHero} */
  const element = template.firstElementChild;
  const refs = getRefs(element);

  if (optionValue) element.setAttribute('option-value', optionValue);

  for (const element of ['heading', 'body']) {
    if (rest[element]) {
      refs[element].innerHTML = rest[element];
      continue;
    }

    refs[element].remove();
  }

  return element;
}

/**
 * Returns the tag filter for fetching a pods product.
 *
 * @param {SubscriptionBuilderOption['value']} collection
 * @param {SubscriptionBuilderOption[]} collectionOptions
 * @returns {string}
 */
function getPodsProductTagFilter(collection, collectionOptions) {
  let filter = "tag:'Pods'";

  /**
   * Note that "Mixed" isn't an actual tag applied to products, hence it being excluded and treated differently here.
   */
  const mixed = 'Mixed';

  if (collection === mixed) {
    /**
     * If mixed is chosen, query by all collection tags.
     */
    const collectionOptionsToFilter = collectionOptions.filter((option) => !(option.value === mixed));

    if (collectionOptionsToFilter.length) {
      filter += ` ${collectionOptionsToFilter.map((option) => `tag:'${option.value}'`).join(' ')}`;
    }
  } else {
    /**
     * If a specific collection is chosen, query by that collection and *not* by the others.
     */
    const otherOptions = collectionOptions.filter(
      (option) => !(option.value === mixed) && !(option.value === collection),
    );

    filter += ` tag:'${collection}'`;

    if (otherOptions.length) {
      filter += ` ${otherOptions.map((option) => `-tag:'${option.value}'`).join(' ')}`;
    }
  }

  return filter;
}

/**
 * Queries the Storefront API.
 *
 * @param {string} query - The GraphQL query.
 * @param {object} variables - The query variables.
 * @returns {Promise}
 */
function queryStorefrontApi(query, variables) {
  return json(
    fetch('https://watchhouse-coffee.myshopify.com/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': '5fc37691e3479eee67aec64bef2051f3',
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    }),
  );
}

/**
 * Sorts selling plan allocations by their number.
 *
 * @param {object} sellingPlanA
 * @param {object} sellingPlanA.sellingPlan
 * @param {string} sellingPlanA.sellingPlan.name
 * @param {object} sellingPlanB
 * @param {object} sellingPlanB.sellingPlan
 * @param {string} sellingPlanB.sellingPlan.name
 */
function sortSellingPlanAllocations({ sellingPlan: { name: planA } }, { sellingPlan: { name: planB } }) {
  return parseInt(planA.match(/\d+/)[0], 10) - parseInt(planB.match(/\d+/)[0], 10);
}

/**
 * The query for fetching a pods product's variants.
 */
const PODS_PRODUCT_QUERY = `
  query ($tag: String!) {
    products (first: 1, query: $tag) {
      nodes {
        handle
        variants (first: 10) {
          nodes {
            title
            id
            subscriptionBuilderHeading: metafield (namespace: "custom", key: "subscription_builder_heading") {
              value
            }
            subscriptionBuilderSubheading: metafield (namespace: "custom", key: "subscription_builder_subheading") {
              value
            }
            subscriptionBuilderBody: metafield (namespace: "custom", key: "subscription_builder_body") {
              value
            }
          }
        }
      }
    }
  }
`;

/**
 * The query for fetching bean products based on a tag.
 */
const BEANS_PRODUCTS_QUERY = `
  query {
    collection (handle: "coffee-and-pods") {
      products (first: 250) {
        nodes {
          title
          handle
          subscriptionBuilderHeading: metafield (namespace: "custom", key: "subscription_builder_heading") {
            value
          }
          subscriptionBuilderSubheading: metafield (namespace: "custom", key: "subscription_builder_subheading") {
            value
          }
          subscriptionBuilderBody: metafield (namespace: "custom", key: "subscription_builder_body") {
            value
          }
          subscriptionBuilderTag: metafield (namespace: "custom", key: "subscription_builder_tag") {
            value
          }
          subscriptionBuilderPriority: metafield (namespace: "custom", key: "subscription_builder_priority") {
            value
          }
        }
      }
    }
  }
`;

/**
 * The query for fetching a bean product's variants.
 */
const BEANS_VARIANTS_QUERY = `
  query ($handle: String!) {
    product (handle: $handle) {
      variants (first: 10) {
        nodes {
          title
          id
          subscriptionBuilderHeading: metafield (namespace: "custom", key: "subscription_builder_heading") {
            value
          }
          subscriptionBuilderSubheading: metafield (namespace: "custom", key: "subscription_builder_subheading") {
            value
          }
          subscriptionBuilderBody: metafield (namespace: "custom", key: "subscription_builder_body") {
            value
          }
        }
      }
    }
  }
`;

/**
 * Returns the product for the frequency step.
 */
const FREQUENCY_PRODUCT_QUERY = `
  query ($handle: String!) {
    product (handle: $handle) {
      variants (first: 10) {
        nodes {
          id
          sellingPlanAllocations (first: 10) {
            nodes {
              sellingPlan {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_PRICING_QUERY = `
  query ($handle: String!) {
    product (handle: $handle) {
      variants (first: 10) {
        nodes {
          id
          sellingPlanAllocations (first: 10) {
            nodes {
              price: checkoutChargeAmount {
                amount
              }
              sellingPlan {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

class SubscriptionBuilderAddOn extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('click', this.refs.remove, () => {
      this.refs.id.disabled = true;

      if (this.subscriptionBuilder) {
        this.subscriptionBuilder.updateSummary();
      }
    });

    this.on('change', this.refs.quantity, () => {
      if (this.subscriptionBuilder) {
        this.subscriptionBuilder.updateSummary();
      }
    });
  }

  /**
   * @returns {SubscriptionBuilder}
   */
  get subscriptionBuilder() {
    return this.closest('subscription-builder');
  }
}

if (!customElements.get('subscription-builder')) customElements.define('subscription-builder', SubscriptionBuilder);
if (!customElements.get('subscription-builder-step'))
  customElements.define('subscription-builder-step', SubscriptionBuilderStep);
if (!customElements.get('subscription-builder-option'))
  customElements.define('subscription-builder-option', SubscriptionBuilderOption);
if (!customElements.get('subscription-builder-hero'))
  customElements.define('subscription-builder-hero', SubscriptionBuilderHero);
if (!customElements.get('subscription-builder-add-on'))
  customElements.define('subscription-builder-add-on', SubscriptionBuilderAddOn);
